export default function PrivacyPolicy() {
  const sectionStyle = { marginBottom: 32 };
  const h2Style = { fontFamily: '"EB Garamond", Georgia, serif', fontSize: 22, fontWeight: 700, color: '#0F172A', marginBottom: 12 };
  const h3Style = { fontFamily: '"EB Garamond", Georgia, serif', fontSize: 18, fontWeight: 700, color: '#0F172A', marginBottom: 8 };
  const pStyle = { fontSize: 14, lineHeight: 1.7, color: '#334155', fontFamily: 'Poppins, sans-serif', marginBottom: 12 };
  const liStyle = { fontSize: 14, lineHeight: 1.7, color: '#334155', fontFamily: 'Poppins, sans-serif', marginBottom: 6 };
  const boldStyle = { fontWeight: 600 };

  return (
    <div style={{ minHeight: '100vh', background: '#F1F0FB', padding: '40px 16px' }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <a href="/" style={{ textDecoration: 'none' }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: 'linear-gradient(135deg, #c9a84c, #F59E0B)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 12px', boxShadow: '0 4px 16px rgba(201,168,76,0.3)',
            }}>
              <span style={{ color: 'white', fontWeight: 700, fontSize: 16, fontFamily: 'Montserrat, sans-serif' }}>PV</span>
            </div>
            <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase', color: '#0F172A' }}>
              PlayVentr
            </div>
          </a>
        </div>

        {/* Card */}
        <div style={{
          background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(24px)',
          border: '1px solid #E2E8F0', borderRadius: 20,
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)', padding: '40px 40px 48px',
        }}>
          <h1 style={{ fontFamily: '"EB Garamond", Georgia, serif', fontSize: 32, fontWeight: 700, color: '#0F172A', marginBottom: 4, textAlign: 'center' }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: 13, color: '#94A3B8', fontFamily: 'Poppins, sans-serif', textAlign: 'center', marginBottom: 36 }}>
            Last Updated: March 30, 2026
          </p>

          <p style={pStyle}>
            This Privacy Policy describes how Forge & Wade LLC ("Company," "we," "us," or "our") collects, uses, stores, shares, and protects information in connection with the PlayVentr platform at app.playventr.com (the "Platform"). By using the Platform, you consent to the practices described in this Privacy Policy.
          </p>
          <p style={pStyle}>
            This Privacy Policy is incorporated into and governed by our <a href="/terms" style={{ color: '#c9a84c', fontWeight: 600 }}>Terms of Service</a>. Capitalized terms not defined here have the meanings given in the Terms of Service.
          </p>

          <div style={sectionStyle}>
            <h2 style={h2Style}>1. Information We Collect</h2>

            <h3 style={h3Style}>1.1 Information You Provide Directly</h3>
            <p style={pStyle}><span style={boldStyle}>Account Information</span></p>
            <p style={pStyle}>When you create an account, we collect:</p>
            <ul style={{ paddingLeft: 20 }}>
              <li style={liStyle}>Full name or display name</li>
              <li style={liStyle}>Email address</li>
              <li style={liStyle}>Password (stored in encrypted form)</li>
              <li style={liStyle}>Account type (Inventor or Company)</li>
            </ul>

            <p style={pStyle}><span style={boldStyle}>Inventor Profile Information</span></p>
            <p style={pStyle}>If you are an Inventor, we may collect:</p>
            <ul style={{ paddingLeft: 20 }}>
              <li style={liStyle}>Name and bio</li>
              <li style={liStyle}>Portfolio URL and avatar</li>
              <li style={liStyle}>Idea descriptions, images, and documentation</li>
              <li style={liStyle}>Pitch materials and presentation content</li>
              <li style={liStyle}>Bill of Materials (BOM) data</li>
              <li style={liStyle}>Payment information (processed by a third-party payment processor; we do not store credit card numbers)</li>
            </ul>

            <p style={pStyle}><span style={boldStyle}>Company Profile Information</span></p>
            <p style={pStyle}>If you are a Company User, we may collect:</p>
            <ul style={{ paddingLeft: 20 }}>
              <li style={liStyle}>Company name, type, and logo</li>
              <li style={liStyle}>Website URL</li>
              <li style={liStyle}>Categories of interest</li>
              <li style={liStyle}>NDA documents and terms</li>
            </ul>

            <p style={pStyle}><span style={boldStyle}>Uploaded Documents</span></p>
            <p style={pStyle}>Users may upload documents including NDA agreements, idea attachments, company logos, and event materials.</p>

            <h3 style={h3Style}>1.2 Information Collected Automatically</h3>
            <p style={pStyle}>When you use the Platform, we automatically collect:</p>
            <ul style={{ paddingLeft: 20 }}>
              <li style={liStyle}>Log data (IP address, browser type, operating system, referring URLs, pages visited, date and time of access)</li>
              <li style={liStyle}>Usage data (features used, pitches generated, matches viewed, events participated in)</li>
              <li style={liStyle}>Device information (device type, screen resolution, unique device identifiers)</li>
              <li style={liStyle}>Cookies and similar tracking technologies (see Section 7)</li>
            </ul>

            <h3 style={h3Style}>1.3 Information from Third-Party Services</h3>
            <p style={pStyle}>When you connect third-party accounts to the Platform, we may receive:</p>
            <ul style={{ paddingLeft: 20 }}>
              <li style={liStyle}><span style={boldStyle}>Gmail:</span> Your Gmail email address, and the ability to send emails on your behalf through the Platform. We access only the scopes you authorize (sending and reading emails). We do not access your contacts, calendar, or other Gmail data beyond the authorized scopes.</li>
              <li style={liStyle}><span style={boldStyle}>Calendly:</span> Your Calendly user profile and scheduling link to facilitate meeting coordination through the Platform.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>2. How We Use Your Information</h2>

            <p style={pStyle}><span style={boldStyle}>To Operate the Platform</span></p>
            <ul style={{ paddingLeft: 20 }}>
              <li style={liStyle}>Create and manage your account</li>
              <li style={liStyle}>Process subscription payments through our third-party payment processor</li>
              <li style={liStyle}>Match Inventors with Company Users using our AI matching algorithms</li>
              <li style={liStyle}>Generate AI-powered pitch scripts and presentation materials</li>
              <li style={liStyle}>Facilitate pitch events, idea submissions, and NDA execution</li>
              <li style={liStyle}>Send emails on your behalf through connected Gmail accounts</li>
              <li style={liStyle}>Coordinate meetings through connected Calendly accounts</li>
              <li style={liStyle}>Provide trend intelligence, BOM estimates, and market analysis</li>
            </ul>

            <p style={pStyle}><span style={boldStyle}>To Improve the Platform</span></p>
            <ul style={{ paddingLeft: 20 }}>
              <li style={liStyle}>Analyze usage patterns to improve features, matching algorithms, and user experience</li>
              <li style={liStyle}>Generate aggregated, anonymized Platform Data (as defined in our Terms of Service)</li>
              <li style={liStyle}>Train and improve our AI systems using aggregated interaction data</li>
              <li style={liStyle}>Conduct internal analytics and research</li>
            </ul>

            <p style={pStyle}><span style={boldStyle}>To Communicate with You</span></p>
            <ul style={{ paddingLeft: 20 }}>
              <li style={liStyle}>Send account-related notifications (subscription confirmations, billing updates, feature changes)</li>
              <li style={liStyle}>Respond to support inquiries</li>
              <li style={liStyle}>Notify you of material changes to our Terms of Service or this Privacy Policy</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>3. How We Share Your Information</h2>

            <h3 style={h3Style}>3.1 Between Inventors and Companies</h3>
            <p style={pStyle}>When Inventors submit ideas to Company Users through events or direct connections, submission materials (idea descriptions, images, pitch content) are shared with the receiving Company User in accordance with any applicable NDA terms. Company profile information (name, type, categories) is visible to connected Inventors.</p>

            <h3 style={h3Style}>3.2 Third-Party Service Providers</h3>
            <p style={pStyle}>We share information with trusted third-party service providers who process data on our behalf:</p>
            <ul style={{ paddingLeft: 20 }}>
              <li style={liStyle}><span style={boldStyle}>Payment Processors:</span> Your payment information is transmitted directly to our payment processor and is not stored on our servers.</li>
              <li style={liStyle}><span style={boldStyle}>Cloud Hosting and Infrastructure:</span> Your data is stored on secure cloud infrastructure provided by third-party hosting services located in the United States.</li>
              <li style={liStyle}><span style={boldStyle}>AI Service Providers:</span> We use third-party AI services to power pitch generation, matching, and analysis features. Data sent to these providers is used solely for generating the requested output.</li>
              <li style={liStyle}><span style={boldStyle}>Email Services:</span> When you connect your Gmail account, emails are sent through Google's Gmail API on your behalf.</li>
              <li style={liStyle}><span style={boldStyle}>Scheduling Services:</span> When you connect Calendly, we access your scheduling information through Calendly's API.</li>
            </ul>

            <h3 style={h3Style}>3.3 Legal Requirements</h3>
            <p style={pStyle}>We may disclose your information if required to do so by law, regulation, legal process, or governmental request, or if we believe disclosure is necessary to protect the rights, property, or safety of the Company, our users, or the public.</p>

            <h3 style={h3Style}>3.4 Business Transfers</h3>
            <p style={pStyle}>In the event of a merger, acquisition, or sale of all or substantially all of the Company's assets, your information may be transferred to the acquiring entity. We will notify you of any such transfer via email or prominent notice on the Platform.</p>

            <h3 style={h3Style}>3.5 Aggregated and Anonymized Data</h3>
            <p style={pStyle}>We may share aggregated, anonymized Platform Data that does not identify any individual user.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>4. Data Storage and Security</h2>

            <h3 style={h3Style}>4.1 Where We Store Your Data</h3>
            <p style={pStyle}>Your data is stored on secure cloud infrastructure provided by third-party hosting services. Servers are located in the United States. Uploaded documents are stored in secure, private storage buckets with access restricted to authenticated users and authorized Company systems.</p>

            <h3 style={h3Style}>4.2 Security Measures</h3>
            <p style={pStyle}>We implement reasonable administrative, technical, and physical safeguards to protect your information, including:</p>
            <ul style={{ paddingLeft: 20 }}>
              <li style={liStyle}>Encrypted data transmission (HTTPS/TLS) for all Platform communications</li>
              <li style={liStyle}>Encrypted password storage</li>
              <li style={liStyle}>Private storage buckets for uploaded documents with access controls</li>
              <li style={liStyle}>Authentication-based access to user data through the Platform's API</li>
              <li style={liStyle}>Encrypted storage of third-party OAuth tokens (Gmail, Calendly)</li>
            </ul>
            <p style={pStyle}>No method of transmission or storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.</p>

            <h3 style={h3Style}>4.3 Data Retention</h3>
            <p style={pStyle}>We retain your information for as long as your account is active or as needed to provide the Platform's services. After account termination, we may retain certain data for a limited period as necessary to comply with legal obligations, resolve disputes, and enforce our agreements. Aggregated, anonymized Platform Data may be retained indefinitely.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>5. Your Rights and Choices</h2>

            <h3 style={h3Style}>5.1 Access and Export</h3>
            <p style={pStyle}>You may access your personal information through your account settings. You may request a full export of Your Content at any time by contacting support@playventr.com.</p>

            <h3 style={h3Style}>5.2 Correction</h3>
            <p style={pStyle}>You may update or correct your personal information at any time through your account settings or by contacting support@playventr.com.</p>

            <h3 style={h3Style}>5.3 Deletion</h3>
            <p style={pStyle}>You may request deletion of your account and personal information by contacting support@playventr.com. Upon receiving a verified deletion request, we will delete your personal information within 45 days, except where retention is required by law.</p>

            <h3 style={h3Style}>5.4 Third-Party Disconnection</h3>
            <p style={pStyle}>You may disconnect your Gmail or Calendly accounts at any time through your Platform settings. Upon disconnection, we will delete the stored OAuth tokens for that service. You may also revoke access directly through the third-party service's settings.</p>

            <h3 style={h3Style}>5.5 Opt-Out of Communications</h3>
            <p style={pStyle}>You may opt out of non-essential communications by using the unsubscribe link in any email. You cannot opt out of essential account communications (such as billing confirmations, security alerts, or Terms/Privacy Policy changes).</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>6. State Privacy Rights</h2>

            <h3 style={h3Style}>6.1 New Jersey Residents</h3>
            <p style={pStyle}>Under the New Jersey Data Privacy Act (effective January 2025), New Jersey residents have the right to: access their personal data, correct inaccuracies, delete their personal data, obtain a copy of their data in a portable format, and opt out of the sale of personal data, targeted advertising, and profiling. To exercise these rights, contact support@playventr.com.</p>

            <h3 style={h3Style}>6.2 California Residents</h3>
            <p style={pStyle}>Under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA), California residents have additional rights including the right to know, right to delete, right to opt out of sale or sharing, and right to non-discrimination. We do not sell personal information in the traditional sense. To exercise your California privacy rights, contact support@playventr.com.</p>

            <h3 style={h3Style}>6.3 Other State Residents</h3>
            <p style={pStyle}>Residents of states with comprehensive privacy laws (including Colorado, Connecticut, Virginia, and others) may have similar rights. We honor these rights consistent with applicable law. Contact support@playventr.com to exercise any privacy rights.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>7. Cookies and Tracking Technologies</h2>
            <ul style={{ paddingLeft: 20 }}>
              <li style={liStyle}><span style={boldStyle}>Essential Cookies:</span> Required for the Platform to function, including authentication cookies that keep you logged in and session cookies. These cannot be disabled.</li>
              <li style={liStyle}><span style={boldStyle}>Analytics Cookies:</span> Used to understand how users interact with the Platform, which features are most used, and where users encounter issues. This data is used to improve the Platform.</li>
            </ul>
            <p style={pStyle}>Most web browsers allow you to control cookies through their settings. Disabling essential cookies may prevent you from using the Platform.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>8. AI Data Processing</h2>
            <p style={pStyle}>The Platform uses artificial intelligence to process your data for the following purposes:</p>
            <ul style={{ paddingLeft: 20 }}>
              <li style={liStyle}>Generating pitch scripts using your idea details and company information</li>
              <li style={liStyle}>Matching Inventors with Company Users based on categories and profiles</li>
              <li style={liStyle}>Analyzing market trends and generating intelligence reports</li>
              <li style={liStyle}>Estimating Bill of Materials costs</li>
            </ul>
            <p style={pStyle}>When the Platform sends data to our AI service provider for processing, the data transmitted may include your idea descriptions, company profiles, and market data relevant to the specific request. This data is sent solely for the purpose of generating the requested output.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>9. Gmail API -- Limited Use Disclosure</h2>
            <p style={pStyle}>PlayVentr's use and transfer to any other app of information received from Google APIs will adhere to the <a href="https://developers.google.com/terms/api-services-user-data-policy" style={{ color: '#c9a84c', fontWeight: 600 }} target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.</p>
            <p style={pStyle}>Specifically:</p>
            <ul style={{ paddingLeft: 20 }}>
              <li style={liStyle}>We only request access to the Gmail scopes necessary for the Platform's functionality (sending emails and reading email metadata)</li>
              <li style={liStyle}>We do not use Gmail data for advertising purposes</li>
              <li style={liStyle}>We do not sell Gmail data to third parties</li>
              <li style={liStyle}>We do not use Gmail data for surveillance or user profiling unrelated to the Platform's core functionality</li>
              <li style={liStyle}>Gmail OAuth tokens are stored encrypted and can be revoked at any time through your Platform settings</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>10. Children's Privacy</h2>
            <p style={pStyle}>The Platform is not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected personal information from a child under 18, we will take steps to delete that information. If you believe a child has provided us with personal information, please contact support@playventr.com.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>11. Third-Party Links and Services</h2>
            <p style={pStyle}>The Platform may contain links to third-party websites and services. We are not responsible for the privacy practices of these third-party sites. We encourage you to read the privacy policies of any third-party site you visit.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>12. International Users</h2>
            <p style={pStyle}>The Platform is operated from the United States. If you access the Platform from outside the United States, you consent to the transfer of your information to the United States, where data protection laws may differ from those in your jurisdiction.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>13. Changes to This Privacy Policy</h2>
            <p style={pStyle}>We may update this Privacy Policy from time to time. Material changes will be communicated via email or through a prominent notice on the Platform at least 30 days before taking effect. Your continued use of the Platform after changes become effective constitutes acceptance of the updated Privacy Policy.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>14. Contact Us</h2>
            <p style={pStyle}>If you have questions about this Privacy Policy, your data, or your privacy rights, contact us at:</p>
            <p style={pStyle}>
              <span style={boldStyle}>Forge & Wade LLC</span><br />
              Email: inquiries@forgeandwade.com<br />
              Website: www.forgeandwade.com
            </p>
            <p style={pStyle}>
              For Platform support, data export requests, or to exercise your privacy rights:<br />
              Support: support@playventr.com
            </p>
            <p style={pStyle}>We aim to respond to all privacy-related inquiries within 45 days, as required by applicable law.</p>
          </div>
        </div>

        {/* Footer links */}
        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 13, fontFamily: 'Poppins, sans-serif' }}>
          <a href="/terms" style={{ color: '#94A3B8', marginRight: 20 }}>Terms of Service</a>
          <a href="/" style={{ color: '#94A3B8' }}>Home</a>
        </div>
      </div>
    </div>
  );
}
